/**
 * SHA-256 checksum validation for downloaded assets.
 */

/**
 * Compute the SHA-256 hash of an ArrayBuffer and return it as a hex string.
 */
export async function computeChecksum(data: ArrayBuffer): Promise<string> {
  // Pass a typed-array VIEW rather than the raw ArrayBuffer: crypto.subtle.digest
  // validates its input with a realm-specific `instanceof ArrayBuffer`, which
  // rejects an ArrayBuffer created in another realm (e.g. jsdom under Node 20 in
  // CI). ArrayBuffer.isView() — used for TypedArrays — is realm-agnostic, so a
  // Uint8Array view is accepted everywhere. The view is zero-copy in production.
  const view = data instanceof Uint8Array ? data : new Uint8Array(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', view);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Validate that the SHA-256 checksum of the given data matches the expected value.
 * Returns true if valid, false otherwise.
 */
export async function validateChecksum(
  data: ArrayBuffer,
  expectedChecksum: string,
): Promise<boolean> {
  const actual = await computeChecksum(data);
  return actual === expectedChecksum.toLowerCase();
}
